import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  DateRange,
  extractDateRange,
  extractDefaultStoreIds,
  extractNumber,
  extractNumberArr,
  getCurrencySymbol,
  getPaymentTotalAmount,
  getPaymentTotalValue,
  HxOrganizationService,
  HxReportService,
  HxStoreService,
  HxUiLabelPipe,
  isoDate,
  OrganizationModel,
  PaymentType,
  ReportProductModel,
  ReportTotalInfo,
  StoreBasicModel,
  toRouterQueryParams
} from 'hx-services';
import { ErrorHandlerService } from '@manager-app/service/error-handler.service';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { DataService } from '@manager-app/service/data.service';
import { HcToastrService } from '@manager-app/service/toastr.service';
import { saveAs } from 'file-saver';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { HxDateRangeSelectComponent, HxEnumSelectComponent, HxLoaderComponent, HxProductCategorySelectComponent, HxStoreSelectComponent } from 'hx-component';
import { FormsModule } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { NgbPagination } from '@ng-bootstrap/ng-bootstrap';

interface ReportProductItem extends ReportProductModel {
  totalAmount?: number;
  totalValue?: number;
}

/**
 * Отчёт по реализации
 */
@Component({
    selector: 'app-received-report.m-grid__item.m-grid__item--fluid.m-wrapper',
    templateUrl: './received-report.component.html',
    styleUrls: ['./received-report.component.css'],
    imports: [HxStoreSelectComponent, FormsModule, HxDateRangeSelectComponent, HxEnumSelectComponent, HxProductCategorySelectComponent, NgSelectComponent, NgIf, NgClass, NgFor, HxLoaderComponent, NgbPagination, DatePipe, TranslocoPipe, HxUiLabelPipe]
})
export class ReceivedReportComponent implements OnInit, OnDestroy {
  PaymentType = PaymentType;
  productShipmentList: ReportProductItem[] = [];
  store?: StoreBasicModel;
  currencySymbol?: string;
  organization?: OrganizationModel;
  limits = DataService.limits;
  pagination = {
    allItemCount: 0,
  };
  isLoading = {
    list: false,
    generate: false
  };
  columns: PaymentType[] = [];
  reportTotalInfo?: ReportTotalInfo;
  params: {
    date?: DateRange,
    page: number,
    limit: number,
    categoryIds?: number[],
    paymentTypes?: PaymentType[],
    storeIds?: number[]
  } = {page: 1, limit: 20};
  brandId?: number;
  storeCurrencyMap = new Map<number, string>();
  storeMap = new Map<number, StoreBasicModel>();
  private sub: Subscription;

  constructor(
    private reportService: HxReportService,
    private aRoute: ActivatedRoute,
    private router: Router,
    private orgService: HxOrganizationService,
    private errorService: ErrorHandlerService,
    private toastr: HcToastrService,
    private storeService: HxStoreService,
    private tr: TranslocoService
  ) {
  }

  ngOnInit() {
    this.brandId = localStorage.getItem('defaultBrandId') ? Number(localStorage.getItem('defaultBrandId')) : undefined;
    this.sub = this.aRoute.queryParamMap.subscribe(paramMap => {
      const asArrFn = (name: string, mapFn: (key: string) => any) => paramMap.get(name) ? paramMap.get(name).split(',').map(mapFn) : undefined;
      this.params.date = extractDateRange('date', paramMap) ?? {from: isoDate(Date.now()), to: isoDate(Date.now())};
      this.params.paymentTypes = asArrFn('paymentTypes', str => PaymentType[str]) ?? [];
      this.params.categoryIds = extractNumberArr('categoryIds', paramMap);
      this.params.limit = extractNumber('limit', paramMap) ?? 20;
      this.params.page = extractNumber('page', paramMap) ?? 1;
      this.params.storeIds = asArrFn('storeIds', id => Number(id)) ?? extractDefaultStoreIds();

      if (this.params.storeIds.length > 0) {
        this.loadStoreAndReport();
      } else {
        this.productShipmentList = [];
      }
    });
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  pageChanged(event: number) {
    this.params.page = event;
    this.navigate(event);
  }

  applyFilters() {
    this.pageChanged(1);
  }

  onStoreChanged(store?: StoreBasicModel | StoreBasicModel[]) {
    if (!store) {
      this.params.categoryIds = [];
      this.params.storeIds = [];
    }
    this.brandId = store[0]?.brandId;
    this.applyFilters();
  }

  generate() {
    this.isLoading.generate = true;
    this.reportService.generateProductShipmentReceived(this.params).subscribe(response => {
      const contentDispositionHeader = response.headers.get('Content-Disposition');
      const result = contentDispositionHeader.split(';')[1].trim().split('=')[1];
      const blob = new Blob([response.body], {type: 'application/octet-stream'});
      saveAs(blob, result);
      this.toastr.success(this.tr.translate('received-report.ts.success'));
      this.isLoading.generate = false;
    }, () => this.isLoading.generate = false);
  }

  private navigate(count: number) {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams(this.params),
      relativeTo: this.aRoute
    });
  }

  private loadProductShipments() {
    this.productShipmentList = [];
    this.isLoading.list = true;
    this.reportService.productShipmentReceived(this.params).subscribe(response => {
      this.columns = response.columns;
      const totalInfo = response.totalInfo;
      totalInfo.payments = totalInfo.payments.filter(p => this.columns.includes(p.type));
      this.reportTotalInfo = totalInfo;
      this.productShipmentList = response.list.map(pr => {
        const item: ReportProductItem = pr;
        item.totalAmount = getPaymentTotalAmount(item.payments);
        item.totalValue = getPaymentTotalValue(item.payments);
        item.payments = item.payments.filter(p => this.columns.includes(p.type));
        return item;
      });
      this.pagination.allItemCount = response.count;
      this.isLoading.list = false;
    }, error => {
      this.isLoading.list = false;
      this.errorService.handler(error);
    });
  }

  private loadCurrentOrganization() {
    this.orgService.getOrganizationById(this.store.organizationId).then(organization => {
      this.organization = organization;
    });
  }

  private loadStoreAndReport() {
    this.storeMap.clear();
    this.storeCurrencyMap.clear();
    this.store = undefined;
    if (this.params.storeIds.length > 1) {
      this.storeService.getStoreByIds(this.params.storeIds).then(stores => {
        stores.forEach(st => {
          this.storeCurrencyMap.set(st.id, getCurrencySymbol(st.currency));
          this.storeMap.set(st.id, st);
        });
      });
    } else {
      this.storeService.getFullStore(this.params.storeIds[0]).then(store => {
        this.store = store;
        this.brandId = store.brandId;
        this.storeCurrencyMap.set(store.id, getCurrencySymbol(store.currency));
        this.storeMap.set(store.id, store);
        this.loadCurrentOrganization();
      });
    }
    this.loadProductShipments();
  }
}
